import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { endpoints } from '../../api/endpoints';
import { useMasters } from '../../hooks/useMasters';
import { usePermission } from '../../hooks/usePermission';

interface Rate {
  id: string;
  metal_id: string;
  purity_id: string | null;
  rate_type: string;
  rate_per_gram: string | null;
  rate_per_fine_gram: string | null;
  effective_from: string;
  status: string;
}

/**
 * Today's rate, on screen at all times.
 *
 * A jeweller checks the rate a dozen times a day — it moves, and every price in
 * the shop follows from it. Making somebody open a page to see it is the kind
 * of thing that gets a system called unusable by people who cannot say why.
 *
 * It also shows when nothing has been published, because a shop that has not
 * set today's rate is a shop that cannot sell, and the till will refuse — far
 * better to learn that here than at the counter with a customer waiting.
 */
/** True when a rate was published today — anything older is worth noticing. */
function setOnDay(at: string): boolean {
  return new Date(at).toDateString() === new Date().toDateString();
}

/**
 * "set 10:42 am" for today's rate, "set Tue 23 Sept" for an older one.
 *
 * A jeweller's first question about a rate on the board is not only what it is
 * but when it was put up: a price from two days ago is a price nobody has
 * checked against the market this morning.
 */
function setAtLabel(at: string): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return '';
  return setOnDay(at)
    ? `set ${d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })}`
    : `set ${d.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' })}`;
}

export function RateStrip() {
  const { can } = usePermission();
  const masters = useMasters();

  const { data } = useQuery({
    queryKey: ['rates'],
    queryFn: () => endpoints.pricing.rates(),
    // The rate is why somebody looks at this strip, so it is refreshed rather
    // than left stale from when the page was opened this morning.
    refetchInterval: 120_000,
    staleTime: 60_000,
  });

  const rates = (data?.data ?? []) as unknown as Rate[];
  const approved = rates.filter((r) => r.status === 'APPROVED' && r.rate_type === 'SALE');

  // The newest approved selling rate for each purity.
  const latest = new Map<string, Rate>();
  for (const rate of approved) {
    const key = `${rate.metal_id}:${rate.purity_id ?? 'any'}`;
    const held = latest.get(key);
    if (!held || rate.effective_from > held.effective_from) latest.set(key, rate);
  }

  const shown = [...latest.values()]
    .map((rate) => ({
      label: masters.purities.find((p) => p.id === rate.purity_id)?.code
        ?? masters.metals.find((m) => m.id === rate.metal_id)?.code
        ?? '',
      value: rate.rate_per_gram ?? rate.rate_per_fine_gram ?? '',
      // When it was set, so a rate nobody has touched since Tuesday is obvious
      // from the strip rather than only from the rates page.
      setAt: rate.effective_from,
    }))
    .filter((r) => r.label && r.value);

  if (shown.length === 0) {
    return can('rate.create') ? (
      <Link to="/rates" className="no-print rounded bg-rail-warn px-2 py-0.5 text-2xs font-semibold text-rail-line hover:underline">
        No rate published — nothing can be priced
      </Link>
    ) : null;
  }

  // The ticker scrolls a long strip, so the content is doubled — when the first
  // copy has slid fully past, the second is in place and the loop is seamless.
  const ticker = [...shown, ...shown];

  return (
    <div className="no-print group flex-1 overflow-hidden">
      <div className="flex w-max items-center gap-8 motion-safe:animate-[rateTicker_40s_linear_infinite] group-hover:[animation-play-state:paused]">
        {ticker.map((r, i) => (
          <span key={`${r.label}-${i}`} className="flex items-baseline gap-1.5 whitespace-nowrap">
            <span className="text-2xs uppercase tracking-wide text-rail-ink/70">{r.label}</span>
            <span className="num text-sm font-semibold text-rail-active">
              ₹{Number(r.value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </span>
            <span className="text-2xs text-rail-ink/50">/g</span>
            <span className={`num text-2xs ${setOnDay(r.setAt) ? 'text-rail-ink/55' : 'text-rail-warn'}`}>
              {setAtLabel(r.setAt)}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}
